import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';

// Heights (in inches) and lengths (in feet) per official Go Rampe chart
// Reference: Conception_Rampe/Charte_Inclinaisons/Charte inclinaisons FR.pdf
const HEIGHTS_IN = [
  3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28,
  29, 30, 32, 34, 36, 38, 40, 42, 45, 48,
];
const LENGTHS_FT = [
  4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50,
];

// Reference ratios shown in chart legend
const REFERENCES = [
  { label: '1:12', degrees: 4.8, kind: 'ok' as const },
  { label: '1:10', degrees: 5.7, kind: 'warn' as const },
  { label: '1:8', degrees: 7.1, kind: 'err' as const },
  { label: '1:6', degrees: 9.6, kind: 'err' as const },
];

// Compute the inclination angle in degrees from height (inches) and length (feet).
// angle = atan(heightIn / (lengthFt * 12)) in degrees, rounded to 1 decimal.
function computeAngle(heightIn: number, lengthFt: number): number {
  const lengthIn = lengthFt * 12;
  if (lengthIn <= heightIn) return 90; // vertical or worse — display capped
  const rad = Math.atan(heightIn / lengthIn);
  return Math.round(((rad * 180) / Math.PI) * 10) / 10;
}

function cellColor(degrees: number): { bg: string; text: string; level: 'ok' | 'warn' | 'err' } {
  if (degrees <= 4.8) return { bg: 'rgba(22,163,74,0.12)', text: '#15803D', level: 'ok' };
  if (degrees <= 7.1) return { bg: 'rgba(217,119,6,0.14)', text: '#92400E', level: 'warn' };
  return { bg: 'rgba(220,38,38,0.14)', text: '#9F1B1B', level: 'err' };
}

interface Props {
  open: boolean;
  onClose: () => void;
  currentHeightIn: number;
  currentRunIn: number;
}

export function InclinationChart({ open, onClose, currentHeightIn, currentRunIn }: Props) {
  const { t } = useTranslation();
  const currentLengthFt = currentRunIn / 12;

  // ESC key closes modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Find the nearest cell to current height + length
  const nearestHeight = HEIGHTS_IN.reduce((prev, curr) =>
    Math.abs(curr - currentHeightIn) < Math.abs(prev - currentHeightIn) ? curr : prev,
  );
  const nearestLength = LENGTHS_FT.reduce((prev, curr) =>
    Math.abs(curr - currentLengthFt) < Math.abs(prev - currentLengthFt) ? curr : prev,
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="chart-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      style={{ background: 'rgba(10, 17, 40, 0.78)', backdropFilter: 'blur(6px)' }}
    >
      {/* Backdrop: click outside panel to close */}
      <button
        type="button"
        aria-label={t('chart.close')}
        className="absolute inset-0 w-full h-full cursor-default"
        style={{ background: 'transparent' }}
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[920px] max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <header
          className="flex items-center justify-between px-5 py-3.5 border-b"
          style={{
            background: 'var(--brand-navy,#0A1128)',
            borderColor: 'rgba(255,255,255,0.05)',
            color: '#fff',
          }}
        >
          <div>
            <h2
              id="chart-title"
              className="text-base font-bold"
              style={{ fontFamily: 'var(--brand-font-display)', letterSpacing: '-0.01em' }}
            >
              {t('chart.title')}
            </h2>
            <p className="text-[11px] opacity-75 mt-0.5">{t('chart.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('chart.close')}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        {/* Legend */}
        <div className="px-5 py-3 border-b border-[var(--line,rgba(15,30,51,0.08))] bg-[var(--canvas,#F5F7FA)]">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="font-semibold text-[var(--t2,rgba(15,30,51,0.66))]">
              {t('chart.legend')}
            </span>
            {REFERENCES.map((r) => {
              const c = cellColor(r.degrees);
              return (
                <span
                  key={r.label}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded font-mono"
                  style={{ background: c.bg, color: c.text }}
                >
                  <strong>{r.label}</strong>
                  <span>= {r.degrees}°</span>
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-[var(--t3,rgba(15,30,51,0.62))] mt-2">
            {t('chart.current', {
              height: currentHeightIn,
              length: currentLengthFt.toFixed(1),
              angle: computeAngle(currentHeightIn, currentLengthFt).toFixed(1),
            })}
          </p>
        </div>

        {/* Scrollable table */}
        <div className="flex-1 overflow-auto p-3">
          <table className="border-collapse text-[11px] tabular-nums w-full">
            <thead>
              <tr>
                <th
                  className="sticky left-0 top-0 z-20 px-2 py-1.5 text-left bg-[var(--brand-navy,#0A1128)] text-white font-semibold border-r border-white/10"
                  style={{ fontFamily: 'var(--brand-font-display)' }}
                >
                  {t('chart.height_label')}
                </th>
                {LENGTHS_FT.map((l) => (
                  <th
                    key={l}
                    className="sticky top-0 z-10 px-1.5 py-1.5 bg-[var(--brand-navy,#0A1128)] text-white font-semibold text-center"
                    style={{ minWidth: '38px' }}
                  >
                    {l}′
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HEIGHTS_IN.map((h) => (
                <tr key={h}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 px-2 py-1 text-right bg-white border-r border-[var(--line)] font-bold text-[var(--ink)]"
                  >
                    {h}″
                  </th>
                  {LENGTHS_FT.map((l) => {
                    const lengthIn = l * 12;
                    if (lengthIn <= h) {
                      // angle would be > 45° — outside ramp practical range
                      return (
                        <td
                          key={l}
                          className="px-1.5 py-1 text-center text-[var(--t3)]"
                          aria-label="—"
                        >
                          —
                        </td>
                      );
                    }
                    const angle = computeAngle(h, l);
                    const c = cellColor(angle);
                    const isCurrent = h === nearestHeight && l === nearestLength;
                    return (
                      <td
                        key={l}
                        className="px-1.5 py-1 text-center font-mono"
                        style={{
                          background: c.bg,
                          color: c.text,
                          outline: isCurrent ? '2px solid var(--brand-primary,#22D3EE)' : undefined,
                          outlineOffset: '-2px',
                          fontWeight: isCurrent ? 700 : 400,
                        }}
                        aria-current={isCurrent ? 'true' : undefined}
                      >
                        {angle}°
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="px-5 py-2.5 border-t border-[var(--line)] text-[11px] text-[var(--t3)] text-center">
          {t('chart.footer')}
        </footer>
      </div>
    </div>
  );
}
